import { ExclamationCircleFilled } from '@ant-design/icons';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { AgentChatStatus } from '../../../components/agent_chat/useAgentChat';
import OWButton from '../../../components/ow_button/OWButton';
import { cn } from '../../../utils/ClassNamesUtils';
import { OSCAR_CONTENT_INSET } from '../../constants/AIAgentConstants';
import { OSCAR_MARK_SIZE } from '../../constants/AIAgentConstants';
import { AgentErrorInfo } from '../../../utils/AgentErrorUtils';
import OscarMark from './OscarMark';

interface OscarConnectionStateProps {
	/** `useAgentChat().status`. */
	status: AgentChatStatus;
	/** `useAgentChat().errorInfo` — only read when `status` is `error`. */
	error?: AgentErrorInfo | null;
	/** `useAgentChat().retry`. Hidden when the error is not retryable. */
	onRetry?: () => void;
	className?: string;
}

/**
 * The connection strip, pinned directly above the composer.
 *
 * Before this, `useAgentChat` computed a `status` and an `errorMessage` that
 * nothing rendered: a failure while connecting left the greeting on screen with
 * a dead composer and no explanation. The strip sits in the one place the user
 * is already looking when the composer will not take their message.
 *
 * It is a strip rather than a full-panel takeover on purpose — reconnecting is
 * usually a blip, and replacing a conversation someone is reading with an error
 * page loses their place for something that resolves in a second.
 *
 * No Figma node covers these states; it is built from existing tokens and the
 * mark used by `OscarThinkingIndicator`, so the two read as the same family.
 */
/**
 * How long a connection may take before it is worth mentioning.
 *
 * Reopening a stored conversation is usually quick, and a strip that appears and
 * vanishes inside a few hundred milliseconds is a flicker, not information. An
 * error is never delayed — that one is always worth saying immediately.
 */
const QUIET_CONNECT_MS = 400;

export const OscarConnectionState = ({
	status,
	error,
	onRetry,
	className,
}: OscarConnectionStateProps) => {
	const [slowEnoughToMention, setSlowEnoughToMention] = useState(false);

	useEffect(() => {
		if (status !== 'connecting' && status !== 'reconnecting') {
			setSlowEnoughToMention(false);
			return undefined;
		}
		const timer = window.setTimeout(() => setSlowEnoughToMention(true), QUIET_CONNECT_MS);
		return () => window.clearTimeout(timer);
	}, [status]);

	if (status === 'ready') {
		return null;
	}

	const strip = cn(
		'flex w-full shrink-0 items-center gap-s border-0 border-t border-solid py-xs',
		OSCAR_CONTENT_INSET
	);

	if (status === 'error') {
		const message = error ? error.message : 'Oscar is unavailable.';
		// An expired OpenWrench session carries no retry label: the token the
		// harness exchanges comes from this app's cache, so retrying re-sends the
		// same dead token. Signing in again is the only thing that helps.
		const retryLabel = error ? error.retryLabel : 'Try again';
		return (
			<div
				role="alert"
				className={cn(strip, 'border-character-danger bg-character-dangerBg', className)}
			>
				<ExclamationCircleFilled className="shrink-0 text-character-danger" />
				<span className="bodyregular min-w-0 flex-1 text-character-primary">{message}</span>
				{retryLabel && onRetry ? (
					<OWButton htmlType="button" size="large" onClick={onRetry} className="shrink-0">
						{retryLabel}
					</OWButton>
				) : null}
			</div>
		);
	}

	if (!slowEnoughToMention) {
		return null;
	}

	return (
		<div role="status" aria-live="polite" className={cn(strip, 'border-grey-4', className)}>
			<OscarMark size={OSCAR_MARK_SIZE.loading} animation="flap" />
			<span className="footnoteregular text-character-secondary">
				{status === 'connecting' ? 'Connecting to Oscar…' : 'Reconnecting…'}
			</span>
		</div>
	);
};

export default OscarConnectionState;

/**
 * Composer placeholder that matches the strip, so the disabled input says why it
 * is disabled instead of still inviting a question. `undefined` keeps the
 * composer's own default.
 */
export const oscarComposerPlaceholder = (status: AgentChatStatus): string | undefined => {
	if (status === 'ready') {
		return undefined;
	}
	return status === 'error' ? 'Oscar is unavailable' : 'Connecting to Oscar…';
};
